/**
 * The tenant admin screens' data (Phase 7b): users and invites, groups,
 * sites and nodes, emergency accounts, device secrets, the audit, usage, and
 * the caller's own offline PIN — query and mutation option factories over the
 * API client (the data-hook pattern, `rundowns.ts`).
 *
 * **Live (D35).** The keys sit under the prefixes `api/changes.ts`'
 * `keysForChange` already invalidates — `['users']`, `['groups']`, `['sites']`,
 * `['nodes']`, `['me']` — so every list here refetches when anyone changes a
 * user, group, site or node. Device secrets, the audit and usage have no
 * change kind; they refetch after the page's own writes and on focus.
 *
 * **A group save** names the `updatedAt` it was loaded at (`ifUpdatedAt`); a
 * 409 re-reads the group and throws `GroupConflict` with it, which the editor
 * turns into the live banner's two choices.
 */
import { ApiError, type ApiClient } from '@norna/api-client/client.js';
import type { CloudUsageResponseWire } from '@norna/api-contract/cloudProductions.js';
import type { DeviceSecretMetaWire } from '@norna/api-contract/deviceSecrets.js';
import type { EmergencyAccountStatusWire } from '@norna/api-contract/offlineAuth.js';
import type { PermissionIdWire } from '@norna/api-contract/permissions.js';
import type { EnrolmentCodeWire, PendingEnrolmentWire, SiteWire, SiteNodeWire } from '@norna/api-contract/sites.js';
import type { GroupWire, InspectInviteResponseWire, UserSummaryWire } from '@norna/api-contract/tenancy.js';
import { infiniteQueryOptions, type QueryClient, type QueryKey, queryOptions } from '@tanstack/react-query';

export const adminKeys = {
  users: ['users'] as const,
  groups: ['groups'] as const,
  groupList: () => [...adminKeys.groups, 'list'] as const,
  group: (groupId: string) => [...adminKeys.groups, 'one', groupId] as const,
  sites: ['sites'] as const,
  siteList: () => [...adminKeys.sites, 'list'] as const,
  emergency: (siteId: string) => [...adminKeys.sites, 'emergency', siteId] as const,
  nodes: (siteId: string) => ['nodes', siteId] as const,
  /** Under the site's nodes: a site's change (a code issued, cancelled, used) and a node's refetch it with them. */
  pendingEnrolments: (siteId: string) => ['nodes', siteId, 'pending'] as const,
  secrets: (studioId: string) => ['device-secrets', studioId] as const,
  audit: ['audit'] as const,
  usage: (month: string) => ['usage', month] as const,
  offlinePin: ['me', 'offline-pin'] as const,
};

const AUDIT_PAGE = 50;
/** The first audit page has no cursor. */
const AUDIT_FIRST_PAGE: string | null = null;

// ─── reads ───

export function usersQuery(api: ApiClient) {
  return queryOptions({ queryKey: adminKeys.users, queryFn: async (): Promise<UserSummaryWire[]> => (await api.call('listUsers', { params: null, query: null, body: null })).users });
}

export function groupsQuery(api: ApiClient) {
  return queryOptions({ queryKey: adminKeys.groupList(), queryFn: async (): Promise<GroupWire[]> => (await api.call('listGroups', { params: null, query: null, body: null })).groups });
}

export type GroupWithMembers = { readonly group: GroupWire; readonly memberIds: readonly string[] };

export function groupQuery(api: ApiClient, groupId: string) {
  return queryOptions({ queryKey: adminKeys.group(groupId), queryFn: async (): Promise<GroupWithMembers> => api.call('getGroup', { params: { groupId }, query: null, body: null }) });
}

export function sitesQuery(api: ApiClient) {
  return queryOptions({ queryKey: adminKeys.siteList(), queryFn: async (): Promise<SiteWire[]> => (await api.call('listSites', { params: null, query: null, body: null })).sites });
}

export function nodesQuery(api: ApiClient, siteId: string) {
  return queryOptions({ queryKey: adminKeys.nodes(siteId), queryFn: async (): Promise<SiteNodeWire[]> => (await api.call('listNodes', { params: { siteId }, query: null, body: null })).nodes });
}

/** The site's codes no server has used yet (never the code): the server list's "waiting…" rows. */
export function pendingEnrolmentsQuery(api: ApiClient, siteId: string) {
  return queryOptions({
    queryKey: adminKeys.pendingEnrolments(siteId),
    queryFn: async (): Promise<PendingEnrolmentWire[]> => (await api.call('listPendingEnrolments', { params: { siteId }, query: null, body: null })).enrolments,
  });
}

export function emergencyQuery(api: ApiClient, siteId: string) {
  return queryOptions({ queryKey: adminKeys.emergency(siteId), queryFn: async (): Promise<EmergencyAccountStatusWire> => api.call('getEmergencyAccount', { params: { siteId }, query: null, body: null }) });
}

export function deviceSecretsQuery(api: ApiClient, studioId: string) {
  return queryOptions({ queryKey: adminKeys.secrets(studioId), queryFn: async (): Promise<DeviceSecretMetaWire[]> => (await api.call('listDeviceSecrets', { params: { studioId }, query: null, body: null })).secrets });
}

export type AuditPage = Awaited<ReturnType<typeof readAuditPage>>;

async function readAuditPage(api: ApiClient, before: string | null) {
  return api.call('listAudit', { params: null, query: before === null ? { limit: AUDIT_PAGE } : { limit: AUDIT_PAGE, before }, body: null });
}

export function auditQuery(api: ApiClient) {
  return infiniteQueryOptions({
    queryKey: adminKeys.audit,
    initialPageParam: AUDIT_FIRST_PAGE,
    queryFn: async ({ pageParam }) => readAuditPage(api, pageParam),
    getNextPageParam: (last: AuditPage): string | null => last.next,
  });
}

export function usageQuery(api: ApiClient, month: string) {
  return queryOptions({ queryKey: adminKeys.usage(month), queryFn: async (): Promise<CloudUsageResponseWire> => api.call('getCloudUsage', { params: null, query: { month }, body: null }) });
}

export function offlinePinQuery(api: ApiClient) {
  return queryOptions({ queryKey: adminKeys.offlinePin, queryFn: async () => api.call('getOfflinePin', { params: null, query: null, body: null }) });
}

/** The set-password page's first read (public: `api` sends no token). */
export function inspectInviteQuery(api: ApiClient, token: string) {
  return queryOptions({
    queryKey: ['invite', token] as const,
    queryFn: async (): Promise<InspectInviteResponseWire> => api.call('inspectInvite', { params: null, query: null, body: { token } }),
    retry: false,
  });
}

// ─── writes ───

/** A mutation that invalidates `keys` when it succeeds (the change channel also does, for everyone else). */
function writes<I, O>(queryClient: QueryClient, keys: (input: I, output: O) => readonly QueryKey[], mutationFn: (input: I) => Promise<O>) {
  return {
    mutationFn,
    onSuccess: async (output: O, input: I): Promise<void> => {
      await Promise.all(keys(input, output).map((queryKey) => queryClient.invalidateQueries({ queryKey })));
    },
  };
}

export function inviteUserMutation(api: ApiClient, queryClient: QueryClient) {
  return writes(queryClient, () => [adminKeys.users, adminKeys.groups], async (input: { readonly email: string; readonly groupIds: readonly string[] }) =>
    api.call('createUser', { params: null, query: null, body: { email: input.email, groupIds: [...input.groupIds] } }),
  );
}

export type UserAction = 'resend' | 'deactivate' | 'reactivate' | 'delete';

/** One of a user row's actions; the answer's user (and, for a resend, the invite). */
export function userActionMutation(api: ApiClient, queryClient: QueryClient) {
  return writes(queryClient, () => [adminKeys.users, adminKeys.groups], async (input: { readonly action: UserAction; readonly userId: string }) => {
    const params = { userId: input.userId };
    switch (input.action) {
      case 'resend':
        return api.call('resendInvite', { params, query: null, body: null });
      case 'deactivate':
        return { ...(await api.call('deactivateUser', { params, query: null, body: null })), invite: null };
      case 'reactivate':
        return { ...(await api.call('reactivateUser', { params, query: null, body: null })), invite: null };
      case 'delete':
        return { ...(await api.call('deleteUser', { params, query: null, body: null })), invite: null };
    }
  });
}

/** Put a user in exactly `groupIds`: add the missing memberships, remove the extra ones. */
export function setUserGroupsMutation(api: ApiClient, queryClient: QueryClient) {
  return writes(queryClient, () => [adminKeys.users, adminKeys.groups], async (input: { readonly userId: string; readonly from: readonly string[]; readonly to: readonly string[] }) => {
    const params = (groupId: string) => ({ groupId, userId: input.userId });
    for (const groupId of input.to.filter((g) => !input.from.includes(g))) await api.call('addGroupMember', { params: params(groupId), query: null, body: null });
    for (const groupId of input.from.filter((g) => !input.to.includes(g))) await api.call('removeGroupMember', { params: params(groupId), query: null, body: null });
  });
}

export function groupMemberMutation(api: ApiClient, queryClient: QueryClient) {
  return writes(queryClient, () => [adminKeys.users, adminKeys.groups], async (input: { readonly groupId: string; readonly userId: string; readonly member: boolean }) => {
    const request = { params: { groupId: input.groupId, userId: input.userId }, query: null, body: null };
    return input.member ? api.call('addGroupMember', request) : api.call('removeGroupMember', request);
  });
}

export function createGroupMutation(api: ApiClient, queryClient: QueryClient) {
  return writes(queryClient, () => [adminKeys.groups], async (input: { readonly name: string }) =>
    (await api.call('createGroup', { params: null, query: null, body: { name: input.name, permissions: [], scope: { kind: 'tenant' } } })).group,
  );
}

/** A save refused because the group moved on: `current` is the group as it now is. */
export class GroupConflict extends Error {
  readonly current: GroupWithMembers;
  constructor(current: GroupWithMembers) {
    super(`conflict: the group was changed at ${current.group.updatedAt}`);
    this.name = 'GroupConflict';
    this.current = current;
  }
}

/** `permissions: null` leaves the group's permissions as they are (not sent) — what a save that did not change them sends. */
export type GroupSave = { readonly groupId: string; readonly ifUpdatedAt: string; readonly name: string; readonly permissions: readonly PermissionIdWire[] | null; readonly scope: GroupWire['scope'] };

export function saveGroupMutation(api: ApiClient, queryClient: QueryClient) {
  return writes(queryClient, () => [adminKeys.groups, adminKeys.users], async (save: GroupSave): Promise<GroupWire> => {
    try {
      const body = { name: save.name, ...(save.permissions === null ? {} : { permissions: [...save.permissions] }), scope: save.scope, ifUpdatedAt: save.ifUpdatedAt };
      return (await api.call('updateGroup', { params: { groupId: save.groupId }, query: null, body })).group;
    } catch (err) {
      if (!(err instanceof ApiError) || err.code !== 'conflict') throw err;
      // Two conflicts share the code: someone else's save (the group moved on), and the Admin group's own fence.
      const current = await api.call('getGroup', { params: { groupId: save.groupId }, query: null, body: null });
      if (current.group.updatedAt === save.ifUpdatedAt) throw err;
      throw new GroupConflict(current);
    }
  });
}

export function deleteGroupMutation(api: ApiClient, queryClient: QueryClient) {
  return writes(queryClient, () => [adminKeys.groups, adminKeys.users], async (input: { readonly groupId: string }) => (await api.call('deleteGroup', { params: input, query: null, body: null })).group);
}

export function createSiteMutation(api: ApiClient, queryClient: QueryClient) {
  return writes(queryClient, () => [adminKeys.sites], async (input: { readonly name: string }) => (await api.call('createSite', { params: null, query: null, body: { name: input.name } })).site);
}

export function renameSiteMutation(api: ApiClient, queryClient: QueryClient) {
  return writes(queryClient, () => [adminKeys.sites], async (input: { readonly siteId: string; readonly name: string }) =>
    (await api.call('updateSite', { params: { siteId: input.siteId }, query: null, body: { name: input.name } })).site,
  );
}

export function deleteSiteMutation(api: ApiClient, queryClient: QueryClient) {
  return writes(queryClient, () => [adminKeys.sites], async (input: { readonly siteId: string }) => (await api.call('deleteSite', { params: input, query: null, body: null })).site);
}

/** "Add server": a one-time code and its `docker run` line; `witness` for the site's LAN witness (D9, D21). Its pending row follows. */
export function enrolmentCodeMutation(api: ApiClient, queryClient: QueryClient) {
  return writes(queryClient, (input: { readonly siteId: string; readonly minutes: number; readonly witness: boolean }) => [adminKeys.nodes(input.siteId)], async (input): Promise<EnrolmentCodeWire> => {
    const body = input.witness ? { expiresInMinutes: input.minutes, role: 'witness' as const } : { expiresInMinutes: input.minutes };
    return (await api.call('createEnrolmentCode', { params: { siteId: input.siteId }, query: null, body })).enrolment;
  });
}

/** Cancel a pending code: a server presenting it is refused from now on. */
export function cancelEnrolmentMutation(api: ApiClient, queryClient: QueryClient) {
  return writes(queryClient, (input: { readonly siteId: string; readonly codeId: string }) => [adminKeys.nodes(input.siteId)], async (input) =>
    (await api.call('cancelEnrolmentCode', { params: input, query: null, body: null })).enrolment,
  );
}

/** "Remove server": revoke for good, no code; `force` past the production guards (the answer's `conflict` says why). */
export function removeNodeMutation(api: ApiClient, queryClient: QueryClient) {
  return writes(queryClient, (input: { readonly siteId: string; readonly nodeId: string; readonly force: boolean }) => [adminKeys.nodes(input.siteId)], async (input) =>
    api.call('removeNode', { params: { siteId: input.siteId, nodeId: input.nodeId }, query: null, body: input.force ? { force: true } : {} }),
  );
}

/** Forget a revoked node's record ("Replaced servers"). */
export function deleteNodeMutation(api: ApiClient, queryClient: QueryClient) {
  return writes(queryClient, (input: { readonly siteId: string; readonly nodeId: string }) => [adminKeys.nodes(input.siteId)], async (input) =>
    (await api.call('deleteNode', { params: input, query: null, body: null })).node,
  );
}

export function swapRolesMutation(api: ApiClient, queryClient: QueryClient) {
  return writes(queryClient, (input: { readonly siteId: string; readonly mainNodeId: string }) => [adminKeys.nodes(input.siteId)], async (input) =>
    api.call('swapRoles', { params: { siteId: input.siteId }, query: null, body: { mainNodeId: input.mainNodeId } }),
  );
}

/** Rename a node, and/or choose its LAN address (null: auto) — cleanup wave G's `updateNode`. */
export function updateNodeMutation(api: ApiClient, queryClient: QueryClient) {
  return writes(queryClient, (input: { readonly siteId: string; readonly nodeId: string; readonly label?: string; readonly lanIp?: string | null }) => [adminKeys.nodes(input.siteId)], async (input) =>
    (
      await api.call('updateNode', {
        params: { siteId: input.siteId, nodeId: input.nodeId },
        query: null,
        body: { ...(input.label === undefined ? {} : { label: input.label }), ...(input.lanIp === undefined ? {} : { lanIp: input.lanIp }) },
      })
    ).node,
  );
}

/** Revoke a node (D21) and get the code for its replacement, in its role. */
export function replaceNodeMutation(api: ApiClient, queryClient: QueryClient) {
  return writes(queryClient, (input: { readonly siteId: string; readonly nodeId: string; readonly minutes: number }) => [adminKeys.nodes(input.siteId)], async (input) =>
    (await api.call('replaceNode', { params: { siteId: input.siteId, nodeId: input.nodeId }, query: null, body: { expiresInMinutes: input.minutes } })).enrolment,
  );
}

/** Create or rotate a site's emergency account; the answer holds the password, shown once. */
export function emergencyAccountMutation(api: ApiClient, queryClient: QueryClient) {
  return writes(queryClient, (input: { readonly siteId: string; readonly rotate: boolean }) => [adminKeys.emergency(input.siteId)], async (input) =>
    api.call('createEmergencyAccount', { params: { siteId: input.siteId }, query: null, body: { rotate: input.rotate } }),
  );
}

export function setDeviceSecretMutation(api: ApiClient, queryClient: QueryClient) {
  return writes(queryClient, (input: { readonly studioId: string; readonly deviceId: string; readonly values: Readonly<Record<string, string>> }) => [adminKeys.secrets(input.studioId)], async (input) =>
    (await api.call('setDeviceSecret', { params: { studioId: input.studioId, deviceId: input.deviceId }, query: null, body: { values: { ...input.values } } })).secret,
  );
}

export function deleteDeviceSecretMutation(api: ApiClient, queryClient: QueryClient) {
  return writes(queryClient, (input: { readonly studioId: string; readonly deviceId: string }) => [adminKeys.secrets(input.studioId)], async (input) =>
    (await api.call('deleteDeviceSecret', { params: input, query: null, body: null })).secret,
  );
}

export function offlinePinMutation(api: ApiClient, queryClient: QueryClient) {
  return writes(queryClient, () => [adminKeys.offlinePin], async (input: { readonly pin: string | null }) =>
    input.pin === null ? api.call('clearOfflinePin', { params: null, query: null, body: null }) : api.call('setOfflinePin', { params: null, query: null, body: { pin: input.pin } }),
  );
}

/** The set-password page's submit (public). */
export function acceptInviteMutation(api: ApiClient) {
  return { mutationFn: async (input: { readonly token: string; readonly password: string }) => api.call('acceptInvite', { params: null, query: null, body: input }) };
}
